import { filmsApi } from "./filmsApi";

/** How long to wait for the next cell before refreshing derived data. */
export const INVALIDATION_DELAY = 1000;

type Dispatch = (action: unknown) => unknown;

type Scheduler = {
  /** Tags waiting to be invalidated on the next flush. */
  tags: Set<string>;
  timer: ReturnType<typeof setTimeout> | undefined;
};

const scheduler: Scheduler = { tags: new Set(), timer: undefined };

/**
 * Refresh derived data once the user stops editing, instead of after every cell.
 *
 * A cell edit invalidates the query that feeds its own grid, so invalidating per
 * keystroke rebuilt the grid's rows mid-typing. That destroyed the in-progress
 * edit and the focus advance, and anyone entering a column of numbers faster
 * than the round-trip lost everything after the first value — sometimes with the
 * dropped digits landing appended to the previous cell.
 *
 * Collecting the tags and flushing them once the typing pauses keeps the rows
 * stable while entry is in flight. The grid shows its own committed values in
 * the meantime — see `usePendingEdits` — so nothing looks stale, and totals
 * catch up a beat later.
 *
 * @param {Function} dispatch Store dispatch.
 * @param {Array} tags Tags to invalidate once editing settles.
 * @param {number} delay Quiet period to wait for.
 */
export function scheduleInvalidation(
  dispatch: Dispatch,
  tags: string[],
  delay: number = INVALIDATION_DELAY
): void {
  tags.forEach((tag) => scheduler.tags.add(tag));

  if (scheduler.timer) clearTimeout(scheduler.timer);
  scheduler.timer = setTimeout(() => {
    const pending = Array.from(scheduler.tags);
    scheduler.tags.clear();
    scheduler.timer = undefined;
    if (pending.length) dispatch(filmsApi.util.invalidateTags(pending));
  }, delay);
}

/**
 * Flush any scheduled invalidation now — for when the user leaves the grid and
 * there is no reason to keep showing figures that are a beat behind.
 *
 * @param {Function} dispatch Store dispatch.
 */
export function flushInvalidation(dispatch: Dispatch): void {
  if (!scheduler.timer) return;
  clearTimeout(scheduler.timer);
  scheduler.timer = undefined;
  const pending = Array.from(scheduler.tags);
  scheduler.tags.clear();
  if (pending.length) dispatch(filmsApi.util.invalidateTags(pending));
}

/** Test seam: forget anything scheduled. */
export function resetInvalidation(): void {
  if (scheduler.timer) clearTimeout(scheduler.timer);
  scheduler.timer = undefined;
  scheduler.tags.clear();
}
